/**
 * Phase 5.3: ValueNormalizationService
 *
 * This service handles value normalization for AI-generated property changes,
 * ensuring that invalid values like "auto" are replaced with calculated pixel values
 * and all numeric values are clamped to valid ranges.
 *
 * Key Features:
 * - Replaces "auto" with calculated fallback values based on modifier direction
 * - Validates and clamps numeric values to 10-2000px range
 * - Handles percentage values appropriately (preserved as-is)
 * - Integrates with Phase 5.2 DimensionIsolationService for modifier context
 *
 * @see Documents/AI_Template_System/Roadmaps/AI_Chat_Enhancement_Roadmap_Phase2.md
 */

import { CSSProperties } from 'react';
import { DimensionAnalysis, DimensionPropertyChange } from './DimensionIsolationService';
import { logger } from '@/utils/logger';

/**
 * Configuration options for the normalization service
 */
export interface NormalizationConfig {
  /** Default unit to use for numeric values (px or %) */
  defaultUnit: 'px' | '%';
  /** Minimum allowed size in pixels */
  minSize: number;
  /** Maximum allowed size in pixels */
  maxSize: number;
  /** Multiplier for "increase" modifier fallback */
  increaseFallbackRatio: number;
  /** Multiplier for "decrease" modifier fallback */
  decreaseFallbackRatio: number;
  /** Default fallback size when current value is 0 or unknown */
  defaultFallbackSize: number;
}

/**
 * Result of normalizing property changes
 */
export interface NormalizationResult {
  /** The normalized property changes */
  changes: DimensionPropertyChange;
  /** Whether any normalization was applied */
  wasNormalized: boolean;
  /** List of normalizations that were applied */
  normalizations: NormalizationAction[];
}

/**
 * Details of a single normalization action
 */
export interface NormalizationAction {
  /** The dimension that was normalized */
  dimension: 'width' | 'height';
  /** Original value before normalization */
  originalValue: string | number | undefined;
  /** New value after normalization */
  normalizedValue: string;
  /** Reason for the normalization */
  reason: 'auto_replacement' | 'invalid_value' | 'clamped' | 'fallback_calculated';
}

/**
 * Default configuration values
 */
const DEFAULT_CONFIG: NormalizationConfig = {
  defaultUnit: 'px',
  minSize: 10,
  maxSize: 2000,
  increaseFallbackRatio: 1.5, // 50% increase
  decreaseFallbackRatio: 0.75, // 25% decrease
  defaultFallbackSize: 100, // Default when current value is unknown
};

/**
 * ValueNormalizationService - Singleton service for value normalization
 */
class ValueNormalizationService {
  private static instance: ValueNormalizationService | null = null;
  private config: NormalizationConfig;

  private constructor(config?: Partial<NormalizationConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    logger.debug("ValueNormalizationService initialized with config", this.config);
  }

  /**
   * Get the singleton instance of ValueNormalizationService
   */
  static getInstance(config?: Partial<NormalizationConfig>): ValueNormalizationService {
    if (!this.instance) {
      this.instance = new ValueNormalizationService(config);
    } else if (config) {
      // Update config if provided
      this.instance.config = { ...this.instance.config, ...config };
    }
    return this.instance;
  }

  /**
   * Reset the singleton instance (useful for testing)
   */
  static resetInstance(): void {
    this.instance = null;
  }

  /**
   * Get current configuration
   */
  getConfig(): NormalizationConfig {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<NormalizationConfig>): void {
    this.config = { ...this.config, ...config };
    console.log('[Phase 5.3] Config updated:', this.config);
  }

  /**
   * Normalizes a dimension value, replacing "auto" with calculated values
   * and clamping numeric values to valid ranges.
   *
   * @param value The value to normalize
   * @param currentValue The current dimension value in pixels
   * @param modifier The intended direction of change from DimensionAnalysis
   * @param dimension Which dimension is being normalized
   * @returns Normalized value as a string with unit
   */
  normalizeDimension(
    value: string | number | undefined,
    currentValue: number,
    modifier: 'increase' | 'decrease' | 'none',
    dimension: 'width' | 'height'
  ): { value: string; action: NormalizationAction | null } {
    console.log('[Phase 5.3] Normalizing dimension:', { value, currentValue, modifier, dimension });

    // Handle undefined/null - calculate fallback
    if (value === undefined || value === null) {
      const fallbackValue = this.calculateFallback(currentValue, modifier, dimension);
      console.log('[Phase 5.3] Calculated fallback for undefined:', fallbackValue);
      return {
        value: fallbackValue,
        action: {
          dimension,
          originalValue: value,
          normalizedValue: fallbackValue,
          reason: 'fallback_calculated',
        },
      };
    }

    const strValue = String(value).trim().toLowerCase();

    // Handle "auto" explicitly - this is the main problem this service addresses
    if (strValue === 'auto') {
      const fallbackValue = this.calculateFallback(currentValue, modifier, dimension);
      console.log('[Phase 5.3] Replacing "auto" with calculated value:', fallbackValue);
      return {
        value: fallbackValue,
        action: {
          dimension,
          originalValue: value,
          normalizedValue: fallbackValue,
          reason: 'auto_replacement',
        },
      };
    }

    // Handle percentage values - preserve as-is
    if (strValue.includes('%')) {
      console.log('[Phase 5.3] Preserving percentage value:', strValue);
      return { value: strValue, action: null };
    }

    // Handle 'inherit', 'initial', 'unset' - treat as auto
    if (['inherit', 'initial', 'unset'].includes(strValue)) {
      const fallbackValue = this.calculateFallback(currentValue, modifier, dimension);
      console.log(`[Phase 5.3] Replacing "${strValue}" with calculated value:`, fallbackValue);
      return {
        value: fallbackValue,
        action: {
          dimension,
          originalValue: value,
          normalizedValue: fallbackValue,
          reason: 'auto_replacement',
        },
      };
    }

    // Extract numeric value
    const numericMatch = strValue.match(/(-?\d+(?:\.\d+)?)/);
    if (!numericMatch) {
      // Non-numeric value that's not a known keyword - use fallback
      console.log('[Phase 5.3] Non-numeric value, using fallback:', value);
      const fallbackValue = this.calculateFallback(currentValue, modifier, dimension);
      return {
        value: fallbackValue,
        action: {
          dimension,
          originalValue: value,
          normalizedValue: fallbackValue,
          reason: 'invalid_value',
        },
      };
    }

    let numericValue = parseFloat(numericMatch[1]);

    // Check if value needs clamping
    const originalNumeric = numericValue;
    const needsClamping = numericValue < this.config.minSize || numericValue > this.config.maxSize;

    // Clamp to valid range
    numericValue = Math.max(this.config.minSize, Math.min(this.config.maxSize, numericValue));

    // Round to integer for pixels (cleaner output)
    numericValue = Math.round(numericValue);

    const normalizedValue = `${numericValue}px`;

    if (needsClamping) {
      console.log('[Phase 5.3] Value clamped:', { original: originalNumeric, clamped: numericValue });
      return {
        value: normalizedValue,
        action: {
          dimension,
          originalValue: value,
          normalizedValue,
          reason: 'clamped',
        },
      };
    }

    return { value: normalizedValue, action: null };
  }

  /**
   * Calculates a fallback value when AI returns "auto" or invalid values.
   * Uses the modifier direction to determine appropriate increase/decrease.
   *
   * @param currentValue Current dimension value in pixels
   * @param modifier Direction of intended change
   * @param dimension Which dimension (for logging)
   * @returns Fallback value as a string with unit
   */
  calculateFallback(
    currentValue: number,
    modifier: 'increase' | 'decrease' | 'none',
    dimension: 'width' | 'height'
  ): string {
    // If current value is 0 or invalid, use default fallback
    const baseValue = currentValue > 0 ? currentValue : this.config.defaultFallbackSize;
    let newValue: number;

    switch (modifier) {
      case 'increase':
        newValue = Math.round(baseValue * this.config.increaseFallbackRatio);
        break;
      case 'decrease':
        newValue = Math.round(baseValue * this.config.decreaseFallbackRatio);
        break;
      default:
        // 'none' - maintain current value
        newValue = Math.round(baseValue);
    }

    // Clamp to valid range
    newValue = Math.max(this.config.minSize, Math.min(this.config.maxSize, newValue));

    console.log('[Phase 5.3] Calculated fallback:', {
      dimension,
      currentValue,
      baseValue,
      modifier,
      newValue,
    });

    return `${newValue}px`;
  }

  /**
   * Normalizes all dimension values in a PropertyChange object.
   * This is the main entry point for processing AI backend responses.
   *
   * @param changes The property changes from AI backend
   * @param currentState Current dimensions in pixels
   * @param dimensionAnalysis Analysis result from Phase 5.2 DimensionIsolationService
   * @returns Normalized property changes with normalization details
   */
  normalizePropertyChanges(
    changes: DimensionPropertyChange,
    currentState: { width: number; height: number },
    dimensionAnalysis: DimensionAnalysis
  ): NormalizationResult {
    const normalized = structuredClone(changes);
    const normalizations: NormalizationAction[] = [];

    console.log('[Phase 5.3] Normalizing property changes:', {
      currentState,
      widthModifier: dimensionAnalysis.widthModifier,
      heightModifier: dimensionAnalysis.heightModifier,
      originalChanges: changes,
    });

    // Process size.width
    if (normalized.size?.width !== undefined) {
      const result = this.normalizeDimension(
        normalized.size.width,
        currentState.width,
        dimensionAnalysis.widthModifier,
        'width'
      );
      normalized.size.width = result.value;
      if (result.action) {
        normalizations.push(result.action);
      }
    }

    // Process size.height
    if (normalized.size?.height !== undefined) {
      const result = this.normalizeDimension(
        normalized.size.height,
        currentState.height,
        dimensionAnalysis.heightModifier,
        'height'
      );
      normalized.size.height = result.value;
      if (result.action) {
        normalizations.push(result.action);
      }
    }

    // Process style.width if present
    if (normalized.style?.width !== undefined) {
      const result = this.normalizeDimension(
        normalized.style.width as string | number,
        currentState.width,
        dimensionAnalysis.widthModifier,
        'width'
      );
      normalized.style.width = result.value;
      if (result.action) {
        normalizations.push(result.action);
      }
    }

    // Process style.height if present
    if (normalized.style?.height !== undefined) {
      const result = this.normalizeDimension(
        normalized.style.height as string | number,
        currentState.height,
        dimensionAnalysis.heightModifier,
        'height'
      );
      normalized.style.height = result.value;
      if (result.action) {
        normalizations.push(result.action);
      }
    }

    // Process responsive position breakpoint styles if present
    if (normalized.responsivePosition) {
      const rp = normalized.responsivePosition as Record<string, unknown>;
      const breakpoints = rp.breakpoints as Record<string, { style?: Record<string, unknown> }> | undefined;

      if (breakpoints) {
        for (const bp of Object.keys(breakpoints)) {
          const bpStyle = breakpoints[bp]?.style;
          if (bpStyle) {
            // Normalize width in breakpoint style
            if (bpStyle.width !== undefined) {
              const result = this.normalizeDimension(
                bpStyle.width as string | number,
                currentState.width,
                dimensionAnalysis.widthModifier,
                'width'
              );
              bpStyle.width = result.value;
              if (result.action) {
                normalizations.push({
                  ...result.action,
                  dimension: 'width',
                  reason: result.action.reason,
                });
              }
            }

            // Normalize height in breakpoint style
            if (bpStyle.height !== undefined) {
              const result = this.normalizeDimension(
                bpStyle.height as string | number,
                currentState.height,
                dimensionAnalysis.heightModifier,
                'height'
              );
              bpStyle.height = result.value;
              if (result.action) {
                normalizations.push({
                  ...result.action,
                  dimension: 'height',
                  reason: result.action.reason,
                });
              }
            }
          }
        }
      }
    }

    const wasNormalized = normalizations.length > 0;

    if (wasNormalized) {
      console.log('[Phase 5.3] Normalized changes:', {
        original: changes,
        normalized,
        normalizations,
      });
    }

    return {
      changes: normalized,
      wasNormalized,
      normalizations,
    };
  }

  /**
   * Convenience method to check if a value needs normalization
   */
  needsNormalization(value: string | number | undefined): boolean {
    if (value === undefined || value === null) {
      return true;
    }

    const strValue = String(value).trim().toLowerCase();

    // "auto" and similar keywords need normalization
    if (['auto', 'inherit', 'initial', 'unset'].includes(strValue)) {
      return true;
    }

    // Percentages don't need normalization
    if (strValue.includes('%')) {
      return false;
    }

    // Extract numeric value to check range
    const numericMatch = strValue.match(/(-?\d+(?:\.\d+)?)/);
    if (!numericMatch) {
      // Non-numeric, non-percentage - needs normalization
      return true;
    }

    const numericValue = parseFloat(numericMatch[1]);

    // Check if out of valid range
    return numericValue < this.config.minSize || numericValue > this.config.maxSize;
  }

  /**
   * Extract numeric value from dimension string
   * Returns null for "auto" or invalid values
   */
  extractNumericValue(value: string | number | undefined): number | null {
    if (value === undefined || value === null) {
      return null;
    }

    if (typeof value === 'number') {
      return value;
    }

    const strValue = String(value).trim().toLowerCase();

    if (strValue === 'auto') {
      return null;
    }

    // Handle percentages - extract the number
    if (strValue.includes('%')) {
      const match = strValue.match(/(-?\d+(?:\.\d+)?)/);
      return match ? parseFloat(match[1]) : null;
    }

    // Extract numeric portion
    const match = strValue.match(/(-?\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : null;
  }

  /**
   * Detect the unit of a dimension value
   */
  detectUnit(value: string | number | undefined): 'px' | '%' | 'auto' | 'unknown' {
    if (value === undefined || value === null) {
      return 'unknown';
    }

    if (typeof value === 'number') {
      return 'px';
    }

    const strValue = String(value).trim().toLowerCase();

    if (strValue === 'auto') {
      return 'auto';
    }

    if (strValue.includes('%')) {
      return '%';
    }

    if (strValue.includes('px') || /^\d+$/.test(strValue)) {
      return 'px';
    }

    return 'unknown';
  }
}

// Export singleton instance
export const valueNormalizationService = ValueNormalizationService.getInstance();

// Also export the class for testing
export { ValueNormalizationService };
